import './interaction-guards.css';

function isTouchLikePointer(event: PointerEvent): boolean {
  return event.pointerType !== 'mouse';
}

function touchInteractionLikely(): boolean {
  return navigator.maxTouchPoints > 0 || window.matchMedia('(pointer: coarse)').matches;
}

function installRangeScrollGuards(): void {
  document.querySelectorAll<HTMLInputElement>('input[type="range"]').forEach((input) => {
    let pointerId: number | null = null;
    let initialValue = '';

    input.addEventListener('pointerdown', (event) => {
      if (!isTouchLikePointer(event)) {
        return;
      }
      pointerId = event.pointerId;
      initialValue = input.value;
    });

    input.addEventListener('pointercancel', (event) => {
      if (pointerId !== event.pointerId) {
        return;
      }
      if (input.value !== initialValue) {
        input.value = initialValue;
        input.dispatchEvent(new Event('input', { bubbles: true }));
      }
      pointerId = null;
      initialValue = '';
    });

    input.addEventListener('pointerup', (event) => {
      if (pointerId === event.pointerId) {
        pointerId = null;
        initialValue = '';
      }
    });
  });
}

function installMovWarningTone(): void {
  const status = document.getElementById('brandPreviewStatus');
  if (!status) {
    return;
  }

  const syncTone = (): void => {
    const text = status.textContent?.trim() ?? '';
    const isMovPreviewWarning = /\bMOV\b/iu.test(text) && /не воспроизвод|не смог/iu.test(text);
    status.classList.toggle('is-mov-warning', isMovPreviewWarning);
  };

  new MutationObserver(syncTone).observe(status, {
    childList: true,
    characterData: true,
    subtree: true,
  });
  syncTone();
}

function installPreviewGestureGuards(): void {
  const circle = document.getElementById('circlePreview');
  if (!(circle instanceof HTMLElement)) {
    return;
  }

  // The editor already has an explicit zoom slider. Wheel zoom makes ordinary
  // desktop page scrolling surprisingly mutate the crop, so let the wheel keep
  // its normal browser behaviour and stop the legacy preview wheel handler.
  circle.addEventListener(
    'wheel',
    (event) => {
      event.stopImmediatePropagation();
    },
    { capture: true, passive: true },
  );

  if (!touchInteractionLikely()) {
    return;
  }

  const hint = document.querySelector<HTMLElement>('.drag-hint');
  const sourceName = document.getElementById('sourceName');
  const button = document.createElement('button');
  button.type = 'button';
  button.className = 'frame-edit-button';
  button.setAttribute('aria-pressed', 'false');

  let editing = false;
  const syncEditing = (): void => {
    circle.classList.toggle('is-touch-editing', editing);
    button.classList.toggle('is-active', editing);
    button.setAttribute('aria-pressed', String(editing));
    button.textContent = editing ? 'Готово' : 'Настроить кадр';
    if (hint) {
      hint.textContent = editing
        ? 'Перемещайте одним пальцем · масштабируйте двумя · затем нажмите «Готово»'
        : 'Кадр зафиксирован, чтобы можно было спокойно листать страницу';
    }
  };

  hint?.insertAdjacentElement('afterend', button);
  button.addEventListener('click', () => {
    editing = !editing;
    syncEditing();
  });

  // When the frame is locked, intercept touch pointerdown before the editor's
  // drag handlers see it. We intentionally do not preventDefault(), so a swipe
  // that starts over the preview remains a normal vertical page scroll.
  circle.addEventListener(
    'pointerdown',
    (event) => {
      if (!editing && isTouchLikePointer(event)) {
        event.stopImmediatePropagation();
      }
    },
    true,
  );

  document.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (editing && target?.closest('[data-tab]')) {
      editing = false;
      syncEditing();
    }
  });

  if (sourceName) {
    new MutationObserver(() => {
      if (editing) {
        editing = false;
        syncEditing();
      }
    }).observe(sourceName, { childList: true, characterData: true, subtree: true });
  }

  syncEditing();
}

function initializeInteractionGuards(): void {
  installPreviewGestureGuards();
  installRangeScrollGuards();
  installMovWarningTone();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeInteractionGuards, { once: true });
} else {
  initializeInteractionGuards();
}
